/* La mia giornata: il foglio di un giorno.
 *
 * E' la schermata che decide se questo programma viene adottato o no. Il
 * difetto 2 dichiarato da Edoardo (i dipendenti imputano svogliatamente) non
 * lo risolve nessun software da solo: qui si tolgono gesti, non si aggiungono
 * campi.
 *
 * ## Il disegno, partito dalle azioni (25 settembre 2026, notte)
 *
 * Chi imputa fa quasi sempre una cosa sola: segnare un blocco di lavoro,
 * spesso sulla stessa pratica di prima. Quindi **un'idea sola: un giorno
 * scelto**. La striscia in alto lo sceglie; sotto, un foglio con il modulo e
 * le ore di quel giorno. Il controllo su piu' giorni sta nel Registro.
 *
 * ## Cliente e prestazione, non la pratica (stessa notte, piu' tardi)
 *
 * Chi imputa pensa "ho fatto il conteggio IMU di Bianchi", non "apro la
 * pratica contabilita' 2026 di Bianchi". Il modulo chiede quelle due cose e
 * **la pratica la ricava**: se ce n'e' una sola adatta la dice e basta, se ce
 * ne sono due chiede quale, se non c'e' dice che nasce e con quale anno. Prima
 * bisognava sceglierla da un elenco e, se mancava, andare in Clienti a
 * crearla: nessuno lo fa per mezz'ora di lavoro. Stesse regole del server
 * (`pratica_per_imputare`), che resta chi decide davvero.
 *
 * **Un blocco ripetuto costa quattro gesti**: cliente e prestazione restano
 * quelli di prima, la durata e' un clic, si scrivono "su cosa" e "con quale
 * risultato", Invio salva e il cursore torna su "su cosa".
 *
 * **Il ritardo si vede e non blocca**: bloccare l'imputazione tardiva non fa
 * imputare prima, fa non imputare affatto.
 */

import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ErroreApi, anagrafica, ore, type DaImputare } from "../api";
import { useCarica } from "../carica";
import { useSessione } from "../sessione";
import { CampoCerca } from "../componenti/CampoCerca";
import { ProponiPrestazione } from "../componenti/ProponiPrestazione";
import { OreDimenticate } from "../componenti/OreDimenticate";
import { PrimiPassi } from "../componenti/PrimiPassi";
import { ErroreCampo, attributiErrore } from "../componenti/ErroreCampo";
import { Striscia, aggiungiGiorni, giornoPerTitolo, lunediDi } from "../componenti/Settimana";
import {
  etichettaBreve,
  euro,
  giorniFa,
  nomeTipo,
  oggi,
  oreEMinuti,
  quanteUnita,
  type Attivita,
  type Cliente,
  type Imputazione,
  type Pratica,
  type TipoPratica,
} from "../dominio";

/** Lo stesso numero di `imputazione.py`: qui si anticipa l'avviso, il server
 * rifiuta comunque dicendo cosa manca. */
const CARATTERI_MINIMI = 8;

const SCORCIATOIE = [15, 30, 45, 60, 90, 120];

/** Come `PRATICHE_SULL_ANNO_PRIMA` nel server: si lavorano l'anno dopo. */
const SULL_ANNO_PRIMA: TipoPratica[] = ["BILANCIO", "DICHIARAZIONE_REDDITI"];

function annoProposto(tipo: TipoPratica): number {
  const anno = new Date().getFullYear();
  return SULL_ANNO_PRIMA.includes(tipo) ? anno - 1 : anno;
}

export function Ore() {
  const { utente } = useSessione();
  const pratiche = useCarica<Pratica[]>(() => anagrafica.pratiche(), []);
  const catalogo = useCarica<Attivita[]>(() => anagrafica.catalogo(), []);
  const clienti = useCarica<Cliente[]>(() => anagrafica.clienti(), []);

  // Dal Registro si arriva qui su un giorno preciso, per correggerlo.
  const [parametri] = useSearchParams();
  const [giorno, setGiorno] = useState(parametri.get("giorno") ?? oggi());
  const [inModifica, setInModifica] = useState<Imputazione | null>(null);
  const [versione, setVersione] = useState(0);

  const lunedi = lunediDi(giorno);
  const settimana = useCarica<Imputazione[]>(
    () => ore.mie(lunedi, aggiungiGiorni(lunedi, 6)),
    [lunedi, versione],
  );

  const perId = useMemo(() => {
    const mappa = new Map<string, Pratica>();
    for (const p of pratiche.dati ?? []) mappa.set(p.identificativo, p);
    return mappa;
  }, [pratiche.dati]);
  const attivitaPerCodice = useMemo(() => {
    const mappa = new Map<string, Attivita>();
    for (const a of catalogo.dati ?? []) mappa.set(a.codice, a);
    return mappa;
  }, [catalogo.dati]);

  // Gli ultimi mesi di chi imputa: servono a proporre, per ogni cliente, le prestazioni che ha gia' usato.
  const storico = useCarica<Imputazione[]>(() => ore.mie(giorniFa(120), oggi()), [versione]);

  const righeSettimana = settimana.dati ?? [];
  const delGiorno = righeSettimana.filter((i) => i.giorno === giorno);
  // "Continua": l'ultima imputazione scritta, per ripartire da li'.
  const ultima = righeSettimana.reduce<Imputazione | undefined>(
    (piuRecente, i) => (piuRecente === undefined || i.giorno > piuRecente.giorno ? i : piuRecente),
    undefined,
  );

  const [erroreEsporta, setErroreEsporta] = useState<string | null>(null);

  /** La settimana mostrata, solo le ore di chi guarda, in un Excel preparato dal
   * server (lo stesso del Registro). Chi vede le ore di tutti non le riceve qui:
   * la pagina e' "le mie ore", quindi la persona si chiede esplicitamente. */
  async function esporta() {
    setErroreEsporta(null);
    try {
      await ore.esportaExcel(
        { dal: lunedi, al: aggiungiGiorni(lunedi, 6), persona: utente?.identificativo },
        `le-mie-ore-settimana-${lunedi}.xlsx`,
      );
    } catch (e) {
      setErroreEsporta(e instanceof ErroreApi ? e.message : "Non sono riuscito a preparare il file.");
    }
  }

  const errore = pratiche.errore ?? catalogo.errore ?? clienti.errore ?? settimana.errore;
  const pronto = pratiche.dati !== undefined && catalogo.dati !== undefined && clienti.dati !== undefined;
  const totaleGiorno = delGiorno.reduce((s, i) => s + i.minuti, 0);

  return (
    <>
      {(errore ?? erroreEsporta) && (
        <p className="errore" role="alert">
          {errore ?? erroreEsporta}
        </p>
      )}

      {!pronto ? (
        errore ? null : <p className="attesa">Un momento.</p>
      ) : (
        <>
          <OreDimenticate
            persona={utente?.identificativo ?? ""}
            versione={versione}
            scegli={(g) => {
              setGiorno(g);
              setInModifica(null);
            }}
          />
          <Striscia
            lunedi={lunedi}
            scelto={giorno}
            imputazioni={righeSettimana}
            scegli={(g) => {
              setGiorno(g);
              setInModifica(null);
            }}
            esporta={esporta}
          />

          <section className="foglio">
            <header className="foglio-testa">
              <h2>{giornoPerTitolo(giorno)}</h2>
              <span className="foglio-totale">
                {totaleGiorno > 0 ? oreEMinuti(totaleGiorno) : "nessuna ora"}
              </span>
            </header>

            <Modulo
              giorno={giorno}
              pratiche={(pratiche.dati ?? []).filter((p) => p.stato === "APERTA")}
              tuttePratiche={perId}
              storico={storico.dati ?? []}
              catalogo={catalogo.dati ?? []}
              clienti={clienti.dati ?? []}
              ricaricaPratiche={pratiche.ricarica}
              ricaricaCatalogo={catalogo.ricarica}
              ultima={ultima}
              inModifica={inModifica}
              annullaModifica={() => setInModifica(null)}
              dopoSalvataggio={async () => {
                setInModifica(null);
                setVersione((v) => v + 1);
              }}
            />

            <ElencoDelGiorno
              righe={delGiorno}
              pratiche={perId}
              attivita={attivitaPerCodice}
              inModifica={inModifica?.identificativo ?? null}
              modifica={setInModifica}
            />
          </section>
          <p className="piede-pagina">
            Per controllare più giorni, un cliente o un mese intero:{" "}
            <Link to="/ore/registro">il registro delle ore</Link>.
          </p>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------- il modulo

interface ModuloProps {
  giorno: string;
  /** Solo le aperte: le sole su cui si imputa. */
  pratiche: Pratica[];
  tuttePratiche: Map<string, Pratica>;
  /** Le ore degli ultimi mesi di chi imputa, per proporre le prestazioni gia' usate su un cliente. */
  storico: Imputazione[];
  catalogo: Attivita[];
  clienti: Cliente[];
  ricaricaPratiche: () => Promise<void>;
  ricaricaCatalogo: () => Promise<void>;
  ultima: Imputazione | undefined;
  inModifica: Imputazione | null;
  annullaModifica: () => void;
  dopoSalvataggio: () => Promise<void>;
}

function Modulo({
  giorno,
  pratiche,
  tuttePratiche,
  storico,
  catalogo,
  clienti,
  ricaricaPratiche,
  ricaricaCatalogo,
  ultima,
  inModifica,
  annullaModifica,
  dopoSalvataggio,
}: ModuloProps) {
  const [clienteId, setClienteId] = useState("");
  const [attivita, setAttivita] = useState("");
  const [praticaScelta, setPraticaScelta] = useState("");
  const [tipoNuova, setTipoNuova] = useState<TipoPratica | "">("");
  const [annoNuova, setAnnoNuova] = useState<number | null>(null);
  const [quantita, setQuantita] = useState("1");
  const [minuti, setMinuti] = useState("60");
  const [oggetto, setOggetto] = useState("");
  const [scopo, setScopo] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [salvato, setSalvato] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [modificaCaricata, setModificaCaricata] = useState<string | null>(null);
  const [erroriCampi, setErroriCampi] = useState<Record<string, string>>({});
  const [continuata, setContinuata] = useState(false);

  function riparteDa(i: Imputazione) {
    const p = tuttePratiche.get(i.pratica_id);
    if (p === undefined) return;
    setClienteId(p.cliente.identificativo);
    setAttivita(i.attivita_codice);
    setPraticaScelta(i.pratica_id);
    setTipoNuova("");
    setAnnoNuova(null);
  }

  // Correggere una riga riempie il modulo con quella.
  if (inModifica !== null && modificaCaricata !== inModifica.identificativo) {
    setModificaCaricata(inModifica.identificativo);
    riparteDa(inModifica);
    setMinuti(String(inModifica.minuti));
    setQuantita(String(inModifica.quantita));
    setOggetto(inModifica.oggetto);
    setScopo(inModifica.scopo);
    setErrore(null);
    setSalvato(null);
    setErroriCampi({});
  }
  if (inModifica === null && modificaCaricata !== null) {
    setModificaCaricata(null);
    setOggetto("");
    setScopo("");
  }

  // "Continua", una volta sola e mai sopra una correzione.
  if (!continuata && inModifica === null && ultima !== undefined) {
    setContinuata(true);
    if (pratiche.some((p) => p.identificativo === ultima.pratica_id)) riparteDa(ultima);
  }

  // Cosa proporre quando si sceglie un cliente: prima quello che ha gia' fatto lui su quel cliente (le piu'
  // recenti), poi le prestazioni dei tipi di pratica aperti del cliente, poi il resto.
  const recenti = useMemo(() => {
    if (clienteId === "") return [] as Attivita[];
    const visti = new Map<string, string>();
    for (const i of storico) {
      if (tuttePratiche.get(i.pratica_id)?.cliente.identificativo !== clienteId) continue;
      const prima = visti.get(i.attivita_codice);
      if (prima === undefined || prima < i.giorno) visti.set(i.attivita_codice, i.giorno);
    }
    return [...visti.entries()]
      .sort((a, b) => (a[1] < b[1] ? 1 : a[1] > b[1] ? -1 : 0))
      .map(([codice]) => catalogo.find((a) => a.codice === codice))
      .filter((a): a is Attivita => a !== undefined)
      .slice(0, 5);
  }, [clienteId, storico, tuttePratiche, catalogo]);

  const vociPrestazione = useMemo(() => {
    const voce = (a: Attivita, gruppo?: string) => ({
      valore: a.codice,
      testo: a.descrizione,
      dettaglio: `${a.codice} · ${
        a.da_rivedere
          ? "nuova, da rivedere"
          : a.prezzo !== null
            ? `${euro(a.prezzo)} ${a.unita ? `a ${a.unita}` : "a prestazione"}`
            : "a ore"
      }`,
      cerca: a.codice,
      gruppo,
    });
    if (clienteId === "") return catalogo.map((a) => voce(a));
    const tipi = new Set(pratiche.filter((p) => p.cliente.identificativo === clienteId).map((p) => p.tipo));
    const giaDette = new Set(recenti.map((a) => a.codice));
    const delCliente = catalogo.filter((a) => !giaDette.has(a.codice) && a.tipi_pratica.some((t) => tipi.has(t)));
    const dette = new Set([...giaDette, ...delCliente.map((a) => a.codice)]);
    return [
      ...recenti.map((a) => voce(a, "Le tue su questo cliente")),
      ...delCliente.map((a) => voce(a, "Per le sue pratiche aperte")),
      ...catalogo.filter((a) => !dette.has(a.codice)).map((a) => voce(a, "Tutte le altre")),
    ];
  }, [clienteId, catalogo, pratiche, recenti]);

  const attivitaScelta = catalogo.find((a) => a.codice === attivita);
  const perUnita = attivitaScelta !== undefined && attivitaScelta.unita !== "";
  const annoCorrente = new Date().getFullYear();

  // Le pratiche aperte del cliente su cui questa prestazione si puo' imputare.
  // L'assistenza ordinaria vale solo per l'anno in corso, come nel server.
  const candidate = useMemo(() => {
    if (attivitaScelta === undefined || clienteId === "") return [];
    const soloAssistenza =
      attivitaScelta.tipi_pratica.length === 1 && attivitaScelta.tipi_pratica[0] === "ASSISTENZA_ORDINARIA";
    return pratiche
      .filter(
        (p) =>
          p.cliente.identificativo === clienteId &&
          attivitaScelta.tipi_pratica.includes(p.tipo) &&
          (!soloAssistenza || p.periodo === annoCorrente),
      )
      .sort((a, b) => b.periodo - a.periodo);
  }, [attivitaScelta, clienteId, pratiche, annoCorrente]);

  const praticaDecisa =
    candidate.length === 1
      ? candidate[0]
      : candidate.find((p) => p.identificativo === praticaScelta);
  const nasce = attivitaScelta !== undefined && clienteId !== "" && candidate.length === 0;
  const tipiPossibili = attivitaScelta?.tipi_pratica ?? [];
  const tipoDellaNuova: TipoPratica | "" = tipiPossibili.length === 1 ? tipiPossibili[0]! : tipoNuova;
  const annoDellaNuova =
    annoNuova ??
    (tipoDellaNuova === ""
      ? null
      : tipoDellaNuova === "ASSISTENZA_ORDINARIA"
        ? annoCorrente
        : annoProposto(tipoDellaNuova));

  if (clienti.length === 0 || catalogo.length === 0) {
    return <PrimiPassi clientiMancanti={clienti.length === 0} catalogoMancante={catalogo.length === 0} />;
  }

  const minutiNumero = Number(minuti);
  const oggettoCorto = oggetto.trim().length > 0 && oggetto.trim().length < CARATTERI_MINIMI;
  const scopoCorto = scopo.trim().length > 0 && scopo.trim().length < CARATTERI_MINIMI;

  async function invia(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setSalvato(null);
    const modulo = evento.currentTarget as HTMLFormElement;
    function segnala(campi: Record<string, string>, messaggio = "Controlla i campi indicati.") {
      setErroriCampi(campi);
      setErrore(messaggio);
      requestAnimationFrame(() => modulo.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
    }
    setErroriCampi({});
    if (!clienteId) return segnala({ cliente: "Scegli il cliente dall'elenco." });
    if (attivitaScelta === undefined) return segnala({ attivita: "Scegli la prestazione dall'elenco." });
    if (!nasce && praticaDecisa === undefined) return setErrore("Scegli su quale pratica va.");
    if (nasce && tipoDellaNuova === "") return setErrore("Scegli il tipo della pratica che nasce.");
    if (!Number.isInteger(minutiNumero) || minutiNumero < 1) {
      return segnala({ minuti: "Inserisci un numero intero di minuti, da 1 in su." });
    }
    const quantitaNumero = perUnita ? Number(quantita) : 1;
    if (!Number.isInteger(quantitaNumero) || quantitaNumero < 1) {
      return segnala({ quantita: "Inserisci un numero intero, da 1 in su." });
    }
    const testi: Record<string, string> = {};
    if (oggetto.trim().length < CARATTERI_MINIMI) testi.oggetto = `Descrivi su cosa hai lavorato con almeno ${CARATTERI_MINIMI} caratteri.`;
    if (scopo.trim().length < CARATTERI_MINIMI) testi.scopo = `Descrivi il risultato con almeno ${CARATTERI_MINIMI} caratteri.`;
    if (Object.keys(testi).length > 0) return segnala(testi);

    setInCorso(true);
    try {
      let praticaId = praticaDecisa?.identificativo;
      if (praticaId === undefined) {
        const nuova = await anagrafica.perImputare({
          cliente_id: clienteId,
          attivita_codice: attivitaScelta.codice,
          tipo: tipoDellaNuova || undefined,
          periodo: annoDellaNuova ?? undefined,
        });
        await ricaricaPratiche();
        praticaId = nuova.identificativo;
        setPraticaScelta(praticaId);
        setAnnoNuova(null);
      }
      const corpo: DaImputare = {
        pratica_id: praticaId,
        giorno: inModifica?.giorno ?? giorno,
        minuti: minutiNumero,
        attivita_codice: attivitaScelta.codice,
        oggetto,
        scopo,
        quantita: quantitaNumero,
      };
      if (inModifica !== null) {
        await ore.correggi(inModifica.identificativo, corpo);
        setSalvato("Correzione salvata.");
      } else {
        await ore.imputa(corpo);
        setSalvato(`Segnati ${oreEMinuti(minutiNumero)}.`);
      }
      // Restano cliente, prestazione e durata: si imputa a raffica. Il
      // cursore torna su "su cosa", pronto per la riga dopo.
      setOggetto("");
      setScopo("");
      await dopoSalvataggio();
      requestAnimationFrame(() => document.getElementById("oggetto")?.focus());
    } catch (e) {
      if (e instanceof ErroreApi) {
        const campi = { ...e.campi };
        if (campi.cliente_id) campi.cliente = campi.cliente_id;
        if (campi.attivita_codice) campi.attivita = campi.attivita_codice;
        segnala(campi, e.message);
      } else setErrore("Non sono riuscito a salvare. Riprova: i dati sono ancora nel modulo.");
    } finally {
      setInCorso(false);
    }
  }

  const inRitardo = (inModifica?.giorno ?? giorno) < giorniFa(1);

  return (
    <form className="imputa" onSubmit={invia} noValidate>
      {inModifica !== null && (
        <p className="riga-avviso riga-avviso-azione">
          <span>Stai correggendo una riga di questo giorno.</span>
          <button type="button" className="tasto tasto-nudo" onClick={annullaModifica}>
            Lascia perdere
          </button>
        </p>
      )}

      <div className="imputa-griglia">
        <div>
          <CampoCerca
            id="cliente"
            errore={erroriCampi.cliente}
            etichetta="Cliente"
            segnaposto="nome, codice o partita IVA"
            nessuna="Nessun cliente con questo nome"
            voci={clienti.map((c) => ({
              valore: c.identificativo,
              testo: c.denominazione,
              cerca: `${c.codice} ${c.codice_fiscale} ${c.partita_iva}`,
            }))}
            valore={clienteId}
            cambia={(v) => {
              setClienteId(v);
              setPraticaScelta("");
              setAnnoNuova(null);
            }}
          />
        </div>

        <div>
          <CampoCerca
            id="attivita"
            errore={erroriCampi.attivita}
            etichetta="Prestazione"
            segnaposto="scrivi, per esempio imu, 730, telefonata"
            nessuna="Nessuna prestazione con questo nome"
            voci={vociPrestazione}
            quante={14}
            valore={attivita}
            cambia={(v) => {
              setAttivita(v);
              setPraticaScelta("");
              setTipoNuova("");
              setAnnoNuova(null);
            }}
          />
          {clienteId !== "" && recenti.length > 0 && (
            <div className="proposte-rapide" role="group" aria-label="Le tue ultime prestazioni su questo cliente">
              <span className="narrativa">Le tue ultime su questo cliente:</span>
              {recenti.map((a) => (
                <button
                  key={a.codice}
                  type="button"
                  className={attivita === a.codice ? "tasto tasto-piccolo scelto" : "tasto tasto-piccolo"}
                  aria-pressed={attivita === a.codice}
                  onClick={() => {
                    setAttivita(a.codice);
                    setPraticaScelta("");
                    setTipoNuova("");
                    setAnnoNuova(null);
                  }}
                >
                  {a.descrizione}
                </button>
              ))}
            </div>
          )}
          <ProponiPrestazione
            dopo={ricaricaCatalogo}
            usa={(codice) => {
              setAttivita(codice);
              setPraticaScelta("");
              setTipoNuova("");
              setAnnoNuova(null);
            }}
          />
        </div>

        {attivitaScelta !== undefined && clienteId !== "" && (
          <div className="imputa-pratica-ricavata" aria-live="polite">
            {candidate.length === 1 && (
              <p>
                Va sulla pratica <strong>{titolo(candidate[0]!)}</strong>.
              </p>
            )}
            {candidate.length > 1 && (
              <>
                <p>Su quale pratica va?</p>
                <div className="segmenti" role="group" aria-label="Pratica">
                  {candidate.map((p) => (
                    <button
                      key={p.identificativo}
                      type="button"
                      className={
                        praticaScelta === p.identificativo ? "tasto tasto-piccolo scelto" : "tasto tasto-piccolo"
                      }
                      aria-pressed={praticaScelta === p.identificativo}
                      onClick={() => setPraticaScelta(p.identificativo)}
                    >
                      {titolo(p)}
                    </button>
                  ))}
                </div>
              </>
            )}
            {nasce && (
              <>
                {tipiPossibili.length > 1 && (
                  <>
                    <p>Nessuna pratica aperta adatta. Di che tipo è il lavoro?</p>
                    <div className="segmenti" role="group" aria-label="Tipo di pratica">
                      {tipiPossibili.map((t) => (
                        <button
                          key={t}
                          type="button"
                          className={tipoNuova === t ? "tasto tasto-piccolo scelto" : "tasto tasto-piccolo"}
                          aria-pressed={tipoNuova === t}
                          onClick={() => {
                            setTipoNuova(t);
                            setAnnoNuova(null);
                          }}
                        >
                          {nomeTipo(t)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {tipoDellaNuova !== "" && annoDellaNuova !== null && (
                  <div className="pratica-nasce">
                    <p>
                      Nasce la pratica{" "}
                      <strong>
                        {nomeTipo(tipoDellaNuova)} {annoDellaNuova}
                      </strong>
                      {tipoDellaNuova === "ASSISTENZA_ORDINARIA" ? ", una per l'anno." : ". Anno giusto?"}
                    </p>
                    {tipoDellaNuova !== "ASSISTENZA_ORDINARIA" && (
                      <div className="segmenti" role="group" aria-label="Anno della pratica">
                        {[annoCorrente - 1, annoCorrente].map((a) => (
                          <button
                            key={a}
                            type="button"
                            className={annoDellaNuova === a ? "tasto tasto-piccolo scelto" : "tasto tasto-piccolo"}
                            aria-pressed={annoDellaNuova === a}
                            onClick={() => setAnnoNuova(a)}
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="imputa-durata">
          <label htmlFor="minuti">Durata, in minuti</label>
          <div className="durata-riga">
            <input
              id="minuti"
              {...attributiErrore("minuti", erroriCampi)}
              type="number"
              min={1}
              step={1}
              value={minuti}
              onChange={(e) => setMinuti(e.target.value)}
            />
            <div className="segmenti" role="group" aria-label="Durate rapide">
              {SCORCIATOIE.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={minutiNumero === m ? "tasto tasto-piccolo scelto" : "tasto tasto-piccolo"}
                  aria-pressed={minutiNumero === m}
                  onClick={() => setMinuti(String(m))}
                >
                  {oreEMinuti(m)}
                </button>
              ))}
            </div>
          </div>
          <ErroreCampo campo="minuti" messaggio={erroriCampi.minuti} />
        </div>

        {perUnita ? (
          <div className="imputa-quantita">
            <label htmlFor="quantita">Quanti</label>
            <div className="quantita-con-unita">
              <input
                id="quantita"
                {...attributiErrore("quantita", erroriCampi)}
                type="number"
                min={1}
                step={1}
                value={quantita}
                onChange={(e) => setQuantita(e.target.value)}
              />
              <span>
                {quanteUnita(Number.parseInt(quantita, 10) || 0, attivitaScelta?.unita ?? "")
                  .split(" ")
                  .slice(1)
                  .join(" ")}
              </span>
            </div>
            <ErroreCampo campo="quantita" messaggio={erroriCampi.quantita} />
          </div>
        ) : (
          <div />
        )}

        <div className="imputa-oggetto">
          <label htmlFor="oggetto">Su cosa</label>
          <input
            id="oggetto"
            {...attributiErrore("oggetto", erroriCampi)}
            value={oggetto}
            placeholder="bilancio consolidato 2025"
            onChange={(e) => setOggetto(e.target.value)}
          />
          <ErroreCampo campo="oggetto" messaggio={erroriCampi.oggetto} />
          {oggettoCorto && <p className="aiuto">Ancora corto: servono {CARATTERI_MINIMI} caratteri.</p>}
        </div>

        <div className="imputa-scopo">
          <label htmlFor="scopo">Con quale risultato</label>
          <input
            id="scopo"
            {...attributiErrore("scopo", erroriCampi)}
            value={scopo}
            placeholder="predisposta la nota integrativa"
            onChange={(e) => setScopo(e.target.value)}
          />
          <ErroreCampo campo="scopo" messaggio={erroriCampi.scopo} />
          {scopoCorto && <p className="aiuto">Ancora corto: servono {CARATTERI_MINIMI} caratteri.</p>}
        </div>
      </div>

      {inRitardo && (
        <p className="riga-avviso">
          Ore di un giorno passato: si può, ma il ritardo viene contato.
        </p>
      )}
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      <div className="imputa-fondo">
        <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
          {inCorso ? "Sto salvando" : inModifica !== null ? "Salva la correzione" : "Segna le ore"}
        </button>
        {salvato && (
          <span className="riga-fatto-breve" role="status">
            {salvato}
          </span>
        )}
      </div>
    </form>
  );
}

/** "Bilancio 2026", "Consulenza 2026, scissione": il cliente e' gia' scritto
 * sopra, nel suo campo. */
function titolo(p: Pratica): string {
  const base = `${nomeTipo(p.tipo)} ${p.periodo}`;
  return p.descrizione ? `${base}, ${p.descrizione}` : base;
}

// ------------------------------------------------------- le ore del giorno

function ElencoDelGiorno({
  righe,
  pratiche,
  attivita,
  inModifica,
  modifica,
}: {
  righe: Imputazione[];
  pratiche: Map<string, Pratica>;
  attivita: Map<string, Attivita>;
  inModifica: string | null;
  modifica: (i: Imputazione) => void;
}) {
  if (righe.length === 0) {
    return <p className="foglio-vuoto">Niente ancora in questo giorno.</p>;
  }
  return (
    <ul className="righe-giorno">
      {righe.map((i) => {
        const pratica = pratiche.get(i.pratica_id);
        const a = attivita.get(i.attivita_codice);
        return (
          <li key={i.identificativo} className={i.identificativo === inModifica ? "in-modifica" : undefined}>
            <span className="righe-durata">{oreEMinuti(i.minuti)}</span>
            <div className="righe-corpo">
              <span className="righe-pratica">
                {pratica ? etichettaBreve(pratica) : "pratica non trovata"}
              </span>
              <span className="righe-attivita">
                {a?.descrizione ?? i.attivita_codice}
                {a?.unita ? `, ${quanteUnita(i.quantita, a.unita)}` : ""}
              </span>
              <span className="righe-narrativa">
                {i.oggetto}: {i.scopo}
              </span>
            </div>
            <div className="righe-azione">
              {i.in_giornata === false && (
                <span className="segno-ritardo">scritta {i.ritardo_giorni} giorni dopo</span>
              )}
              {i.fatturata ? (
                <span className="segno-fatturata">fatturata</span>
              ) : (
                <button type="button" className="tasto tasto-nudo" onClick={() => modifica(i)}>
                  Correggi
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
